import { useRef, useEffect, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

interface useFlightsListProps {
  flightsCount: number;
  defaultVisibleCount?: number;
  lockDelay?: number;
  visibleFlights: number;
  setVisibleFlights: (value: number) => void;
}
/** Хук позволяет рендерить список перелетов по частям:
 * 1. Создаём ref для Intersection Observer
 * 2. Когда пользователь пролистал экран до триггера, рендерим новые перелёты
 *
 * @param flightsCount общее количество элементов
 * @param lockDelay задерка, чтобы избежать быстрого увеличения visibleFlights
 * @param defaultVisibleCount количество видимых элементов
 */
export const useFlightsList = ({
  flightsCount,
  lockDelay = 2000,
  defaultVisibleCount = 3,
  visibleFlights,
  setVisibleFlights,
}: useFlightsListProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const entry = useIntersectionObserver(ref, { rootMargin: '100px' });
  const isVisible = !!entry?.isIntersecting;

  const [lock, setLock] = useState(false);

  const handleLoadMore = () => {
    setLock(true);

    const newFlightAmount =
      flightsCount < visibleFlights + defaultVisibleCount
        ? flightsCount || defaultVisibleCount
        : visibleFlights + defaultVisibleCount;

    setVisibleFlights(newFlightAmount);

    setTimeout(() => setLock(false), lockDelay);
  };

  useEffect(() => {
    if (isVisible && flightsCount > visibleFlights && !lock) {
      handleLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, lock, visibleFlights, flightsCount]);

  return { visibleFlights, onClick: handleLoadMore, observerTriggerRef: ref };
};
